import React, { useEffect, useState, useCallback } from 'react';
import { invoiceAPI, labelAPI } from '../../lib/api';
import { formatCurrency, formatCurrencyFull, formatDate, formatMonth } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import StatusBadge from '../../components/ui/StatusBadge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
    FileText, 
    Loader2,
    Download,
    CheckCircle,
    XCircle,
    Clock,
    Eye,
    DollarSign,
    Upload,
    Receipt
} from 'lucide-react';
import PaymentDetailsModal from '../../components/PaymentDetailsModal';
import LabelProfileModal from '../../components/LabelProfileModal';

const statusConfig = {
    pending_finance: { label: 'Pending Finance', color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/20', icon: Clock },
    pending_founder: { label: 'Pending Founder', color: 'bg-blue-500/20 text-blue-500 border-blue-500/20', icon: Eye },
    vetted_finance: { label: 'Finance Approved', color: 'bg-teal-500/20 text-teal-500 border-teal-500/20', icon: CheckCircle },
    rejected_finance: { label: 'Finance Rejected', color: 'bg-red-500/20 text-red-500 border-red-500/20', icon: XCircle },
    approved: { label: 'Approved', color: 'bg-secondary/20 text-secondary border-secondary/20', icon: CheckCircle },
    paid: { label: 'Paid', color: 'bg-primary/20 text-primary border-primary/20', icon: DollarSign },
    rejected: { label: 'Rejected', color: 'bg-destructive/20 text-destructive border-destructive/20', icon: XCircle }
};

const InvoicesPage = () => {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLabel, setSelectedLabel] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    
    // Mark as Paid modal state
    const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [paymentComment, setPaymentComment] = useState('');
    const [utrNumber, setUtrNumber] = useState('');
    const [paidOnDate, setPaidOnDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [bankReceiptFile, setBankReceiptFile] = useState(null);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [viewPaymentInvoice, setViewPaymentInvoice] = useState(null);

    const isFounder = user?.role === 'founder';
    // Finance permissions are STRICT: only the Finance role (and admin) can vet/mark-paid.
    // Founders are intentionally excluded — payment marking is reserved for Finance.
    const isFinance = user?.role === 'finance' || user?.role === 'admin';
    const isAdmin = user?.role === 'admin' || isFounder;

    const fetchData = useCallback(async () => {
        try {
            const params = {};
            if (selectedLabel !== 'all') params.label_id = selectedLabel;
            if (selectedStatus !== 'all') params.status = selectedStatus;

            const [invoicesRes, labelsRes] = await Promise.all([
                invoiceAPI.getAll(params),
                labelAPI.getAll()
            ]);
            setInvoices(invoicesRes.data);
            setLabels(labelsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    }, [selectedLabel, selectedStatus]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Reject-with-remarks dialog (Finance + Founder) ---
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectInvoice, setRejectInvoice] = useState(null);
    const [rejectRole, setRejectRole] = useState('finance'); // 'finance' | 'founder'
    const [rejectRemarks, setRejectRemarks] = useState('');
    const [rejecting, setRejecting] = useState(false);

    const openRejectDialog = (invoice, role) => {
        setRejectInvoice(invoice);
        setRejectRole(role);
        setRejectRemarks('');
        setRejectDialogOpen(true);
    };

    const submitReject = async () => {
        if (!rejectInvoice) return;
        if (!rejectRemarks.trim()) {
            toast.error('Please enter a reason for rejection');
            return;
        }
        setRejecting(true);
        try {
            if (rejectRole === 'finance') {
                await invoiceAPI.financeVet(rejectInvoice.id, 'reject', rejectRemarks.trim());
            } else {
                await invoiceAPI.founderApprove(rejectInvoice.id, 'reject', rejectRemarks.trim());
            }
            toast.success('Invoice rejected with remarks');
            setRejectDialogOpen(false);
            setRejectInvoice(null);
            setRejectRemarks('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject invoice');
        } finally {
            setRejecting(false);
        }
    };

    // Label Profile modal — read-only KYC viewer launched from any label name click
    const [labelProfileId, setLabelProfileId] = useState(null);
    const [labelProfileOpen, setLabelProfileOpen] = useState(false);
    const openLabelProfile = (labelId) => {
        if (!labelId) return;
        setLabelProfileId(labelId);
        setLabelProfileOpen(true);
    };

    // --- Approve-with-remarks dialog (Finance + Founder) ---
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approveInvoice, setApproveInvoice] = useState(null);
    const [approveRole, setApproveRole] = useState('finance');
    const [approveRemarks, setApproveRemarks] = useState('');
    const [approving, setApproving] = useState(false);

    const openApproveDialog = (invoice, role) => {
        setApproveInvoice(invoice);
        setApproveRole(role);
        setApproveRemarks('');
        setApproveDialogOpen(true);
    };

    const submitApprove = async () => {
        if (!approveInvoice) return;
        setApproving(true);
        try {
            const remarks = approveRemarks.trim() || undefined;
            if (approveRole === 'finance') {
                await invoiceAPI.financeVet(approveInvoice.id, 'approve', remarks);
            } else {
                await invoiceAPI.founderApprove(approveInvoice.id, 'approve', remarks);
            }
            toast.success(`Invoice approved by ${approveRole === 'finance' ? 'Finance' : 'Founder'}`);
            setApproveDialogOpen(false);
            setApproveInvoice(null);
            setApproveRemarks('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve invoice');
        } finally {
            setApproving(false);
        }
    };

    const handleStatusChange = async (invoiceId, newStatus) => {
        try {
            await invoiceAPI.updateStatus(invoiceId, newStatus);
            toast.success(`Invoice ${newStatus}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        }
    };
    
    const openMarkPaidDialog = (invoice) => {
        setSelectedInvoice(invoice);
        setPaymentComment('');
        setBankReceiptFile(null);
        setMarkPaidDialogOpen(true);
    };
    
    const handleMarkPaid = async () => {
        if (!selectedInvoice) return;
        
        try {
            setUploadingReceipt(true);
            
            // Mark as paid with payment details (UTR / Paid On / Remarks)
            await invoiceAPI.markPaid(selectedInvoice.id, {
                payment_comment: paymentComment,
                utr_number: utrNumber,
                paid_on: paidOnDate,
            });
            
            // Then upload bank receipt if provided
            if (bankReceiptFile) {
                await invoiceAPI.uploadBankReceipt(selectedInvoice.id, bankReceiptFile);
            }
            
            toast.success('Invoice marked as paid successfully');
            setMarkPaidDialogOpen(false);
            setSelectedInvoice(null);
            setPaymentComment('');
            setUtrNumber('');
            setPaidOnDate(new Date().toISOString().slice(0, 10));
            setBankReceiptFile(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to mark invoice as paid');
        } finally {
            setUploadingReceipt(false);
        }
    };

    const handleDownload = async (invoiceId) => {
        try {
            const response = await invoiceAPI.downloadAttachment(invoiceId);
            const { filename, data } = response.data;
            
            // Decode base64 and download
            const byteCharacters = atob(data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray]);
            
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
        } catch (error) {
            toast.error('Failed to download attachment');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="invoices-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-heading font-bold tracking-tight">Invoice Management</h1>
                <p className="text-muted-foreground">Review and manage invoices from record labels</p>
            </div>

            {/* Filters */}
            <Card className="bg-card/50 border-border/40">
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4">
                        <Select value={selectedLabel} onValueChange={setSelectedLabel}>
                            <SelectTrigger className="w-48 bg-background/50" data-testid="filter-label">
                                <SelectValue placeholder="Filter by label" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Labels</SelectItem>
                                {labels.map((label) => (
                                    <SelectItem key={label.id} value={label.id}>
                                        {label.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="w-48 bg-background/50" data-testid="filter-status">
                                <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending_finance">Pending Finance</SelectItem>
                                <SelectItem value="pending_founder">Pending Founder</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                                <SelectItem value="rejected_finance">Rejected by Finance</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Invoices ({invoices.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="rounded-lg border border-border/40 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Invoice #</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Label</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Period</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Amount</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Status</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Attachment</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Created</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {invoices.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            No invoices found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    invoices.map((invoice) => {
                                        const status = statusConfig[invoice.status] || statusConfig.pending;
                                        const StatusIcon = status.icon;
                                        
                                        return (
                                            <TableRow key={invoice.id} className="hover:bg-muted/10 border-border/40">
                                                <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        onClick={() => openLabelProfile(invoice.label_id)}
                                                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full hover:bg-primary/20 hover:underline transition-colors cursor-pointer"
                                                        title="View label profile"
                                                        data-testid={`open-label-profile-${invoice.id}`}
                                                    >
                                                        {invoice.label_name}
                                                    </button>
                                                </TableCell>
                                                <TableCell>{formatMonth(invoice.month)}</TableCell>
                                                <TableCell className="text-right font-mono font-medium">
                                                    {formatCurrencyFull(invoice.amount, invoice.currency)}
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge status={invoice.status} />
                                                </TableCell>
                                                <TableCell>
                                                    {invoice.attachment_filename ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDownload(invoice.id)}
                                                            className="text-primary"
                                                            data-testid={`download-${invoice.id}`}
                                                        >
                                                            <Download className="w-4 h-4 mr-1" />
                                                            {invoice.attachment_filename.slice(0, 20)}...
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">No file</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm">
                                                    {formatDate(invoice.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    {/* Finance can vet pending_finance invoices */}
                                                    {invoice.status === 'pending_finance' && isFinance && (
                                                        <div className="flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-secondary hover:text-secondary"
                                                                onClick={() => openApproveDialog(invoice, 'finance')}
                                                                data-testid={`finance-approve-${invoice.id}`}
                                                            >
                                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                                Vet
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-destructive hover:text-destructive"
                                                                onClick={() => openRejectDialog(invoice, 'finance')}
                                                                data-testid={`finance-reject-${invoice.id}`}
                                                            >
                                                                <XCircle className="w-4 h-4 mr-1" />
                                                                Reject
                                                            </Button>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Founder can approve pending_founder invoices */}
                                                    {invoice.status === 'pending_founder' && isFounder && (
                                                        <div className="flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-secondary hover:text-secondary"
                                                                onClick={() => openApproveDialog(invoice, 'founder')}
                                                                data-testid={`founder-approve-${invoice.id}`}
                                                            >
                                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-destructive hover:text-destructive"
                                                                onClick={() => openRejectDialog(invoice, 'founder')}
                                                                data-testid={`founder-reject-${invoice.id}`}
                                                            >
                                                                <XCircle className="w-4 h-4 mr-1" />
                                                                Reject
                                                            </Button>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Finance can mark approved invoices as paid with comment and receipt */}
                                                    {invoice.status === 'approved' && isFinance && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-primary"
                                                            onClick={() => openMarkPaidDialog(invoice)}
                                                            data-testid={`markpaid-${invoice.id}`}
                                                        >
                                                            <DollarSign className="w-4 h-4 mr-1" />
                                                            Mark Paid
                                                        </Button>
                                                    )}

                                                    {/* Paid invoices — view payment details + receipt */}
                                                    {invoice.status === 'paid' && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-green-500 hover:bg-green-500/10"
                                                            onClick={() => { setViewPaymentInvoice(invoice); }}
                                                            data-testid={`view-payment-${invoice.id}`}
                                                        >
                                                            <Receipt className="w-4 h-4 mr-1" />
                                                            Payment
                                                        </Button>
                                                    )}
                                                    
                                                    {/* Show finance / founder remarks if available */}
                                                    {invoice.founder_remarks && (
                                                        <div className="text-xs text-secondary mt-1">
                                                            Founder: {invoice.founder_remarks}
                                                        </div>
                                                    )}
                                                    {invoice.finance_notes && !invoice.founder_remarks && (
                                                        <div className="text-xs text-muted-foreground mt-1">
                                                            Finance: {invoice.finance_notes}
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
            
            {/* Mark as Paid Dialog */}
            <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Mark Invoice as Paid</DialogTitle>
                        <DialogDescription>
                            Add payment details and upload bank receipt for invoice {selectedInvoice?.invoice_number}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Paid On + UTR */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="paid-on">Paid On</Label>
                                <Input
                                    id="paid-on"
                                    type="date"
                                    value={paidOnDate}
                                    onChange={(e) => setPaidOnDate(e.target.value)}
                                    data-testid="paid-on-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="utr-number">UTR / Reference Number</Label>
                                <Input
                                    id="utr-number"
                                    placeholder="e.g., AXISN0123456789"
                                    value={utrNumber}
                                    onChange={(e) => setUtrNumber(e.target.value)}
                                    data-testid="utr-input"
                                />
                            </div>
                        </div>

                        {/* Query / Remarks */}
                        <div className="space-y-2">
                            <Label htmlFor="payment-comment">Query / Remarks (Optional)</Label>
                            <Textarea
                                id="payment-comment"
                                placeholder="e.g., Paid via NEFT • Bank: HDFC • Hold approved by founder"
                                value={paymentComment}
                                onChange={(e) => setPaymentComment(e.target.value)}
                                rows={3}
                                data-testid="payment-remarks-input"
                            />
                        </div>
                        
                        {/* Bank Receipt Upload */}
                        <div className="space-y-2">
                            <Label htmlFor="bank-receipt">Bank Receipt (Optional)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    id="bank-receipt"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setBankReceiptFile(e.target.files?.[0] || null)}
                                    className="flex-1"
                                />
                                {bankReceiptFile && (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Upload bank statement or payment receipt (PDF, JPG, PNG)
                            </p>
                        </div>
                        
                        {/* Invoice Details Summary */}
                        <div className="p-3 bg-muted/30 rounded-lg space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Label:</span>
                                <button
                                    type="button"
                                    onClick={() => openLabelProfile(selectedInvoice?.label_id)}
                                    className="font-medium text-primary hover:underline cursor-pointer text-right"
                                    data-testid="markpaid-label-link"
                                >
                                    {selectedInvoice?.label_name}
                                </button>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Amount:</span>
                                <span className="font-medium">
                                    {selectedInvoice && formatCurrencyFull(selectedInvoice.amount, selectedInvoice.currency)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Period:</span>
                                <span className="font-medium">{selectedInvoice && formatMonth(selectedInvoice.month)}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setMarkPaidDialogOpen(false)}
                            disabled={uploadingReceipt}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleMarkPaid}
                            disabled={uploadingReceipt}
                            className="min-w-[120px]"
                        >
                            {uploadingReceipt ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Mark as Paid
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Approve-with-remarks dialog (Finance + Founder share the same UI) */}
            <Dialog open={approveDialogOpen} onOpenChange={(o) => !approving && setApproveDialogOpen(o)}>
                <DialogContent data-testid="approve-invoice-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-secondary">
                            <CheckCircle className="w-5 h-5" />
                            {approveRole === 'finance' ? 'Vet' : 'Approve'} Invoice {approveInvoice?.invoice_number}
                        </DialogTitle>
                        <DialogDescription>
                            Add an optional remark with the approval. It will be saved against this invoice and visible to the label and finance team.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Label htmlFor="approve-remarks">Remarks (Optional)</Label>
                        <Textarea
                            id="approve-remarks"
                            placeholder="e.g., Verified against revenue file • amount matches reporting"
                            value={approveRemarks}
                            onChange={(e) => setApproveRemarks(e.target.value)}
                            rows={4}
                            data-testid="approve-remarks-input"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setApproveDialogOpen(false)} disabled={approving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submitApprove}
                            disabled={approving}
                            className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
                            data-testid="submit-approve-btn"
                        >
                            {approving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving</> : <><CheckCircle className="w-4 h-4 mr-2" /> {approveRole === 'finance' ? 'Vet' : 'Approve'}</>}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reject-with-remarks dialog (Finance + Founder share the same UI) */}
            <Dialog open={rejectDialogOpen} onOpenChange={(o) => !rejecting && setRejectDialogOpen(o)}>
                <DialogContent data-testid="reject-invoice-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <XCircle className="w-5 h-5" />
                            Reject Invoice {rejectInvoice?.invoice_number}
                        </DialogTitle>
                        <DialogDescription>
                            Enter the reason for rejection. The label will see this remark on their dashboard and invoices page.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Label htmlFor="reject-remarks">Reason / Remarks <span className="text-destructive">*</span></Label>
                        <Textarea
                            id="reject-remarks"
                            placeholder="e.g., Amount exceeds invoiced revenue for the month — please attach the source breakdown and resubmit."
                            value={rejectRemarks}
                            onChange={(e) => setRejectRemarks(e.target.value)}
                            rows={4}
                            data-testid="reject-remarks-input"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={rejecting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submitReject}
                            disabled={rejecting || !rejectRemarks.trim()}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="submit-reject-btn"
                        >
                            {rejecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rejecting</> : <>Reject Invoice</>}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Payment details modal for paid invoices */}
            <PaymentDetailsModal
                invoice={viewPaymentInvoice}
                open={!!viewPaymentInvoice}
                onOpenChange={(open) => { if (!open) setViewPaymentInvoice(null); }}
            />

            {/* Label Profile modal — read-only KYC viewer for invoice verification */}
            <LabelProfileModal
                open={labelProfileOpen}
                onOpenChange={setLabelProfileOpen}
                labelId={labelProfileId}
            />
        </div>
    );
};

export default InvoicesPage;
